import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAtlas } from './store.ts';
import { useShortcuts } from './shortcuts.ts';
import { startClock } from './clock.ts';
import { startSims } from './sims.ts';
import { startLive } from './live.ts';
import { startViewport } from '../viewport/host.ts';
import { IconProvider, TipProvider } from '../ui/primitives.tsx';
import { TopBar } from '../features/TopBar.tsx';
import { LeftPanel } from '../features/LeftPanel.tsx';
import { Inspector } from '../features/Inspector.tsx';
import { Timeline } from '../features/Timeline.tsx';
import { Traces } from '../features/Traces.tsx';
import { Methods } from '../features/Methods.tsx';
import { Hud } from '../features/Hud.tsx';
import { Labels } from '../features/Labels.tsx';
import { ElectrodeSites } from '../features/ElectrodeSites.tsx';
import { Palette } from '../features/Palette.tsx';
import { Help } from '../features/Help.tsx';
import { GpuNotice } from '../features/GpuNotice.tsx';
import { HoverCard } from '../features/HoverCard.tsx';
import { Loading, PresentHint } from '../features/Loading.tsx';
import { SectionMarks } from '../features/SectionMarks.tsx';
import { Toasts } from '../features/Toast.tsx';
import { Gallery } from '../features/Gallery.tsx';
import { Tour } from '../features/Tour.tsx';
import { Highlights } from '../features/Highlights.tsx';
import { MoleculeDriver } from '../features/Molecule.tsx';
import { TextBoard } from '../features/Text.tsx';
import { Journey } from '../features/Journey.tsx';

const params = new URLSearchParams(location.search);

/** How much of the viewport each panel covers, so the camera frames the clear area. */
function useInsets(root: React.RefObject<HTMLDivElement | null>) {
  const setInsets = useAtlas((s) => s.setInsets);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const measure = () => {
      if (useAtlas.getState().presenting) return setInsets({ top: 0, right: 0, bottom: 0, left: 0 });
      const base = el.getBoundingClientRect();
      const get = (sel: string) => {
        const p = el.querySelector<HTMLElement>(sel);
        return p && !p.classList.contains('collapsed') ? p : null;
      };
      // A panel laid out as a sheet over the bottom (features/phone.css sets --sheet: 1 on a phone) clears the bottom, not its side.
      const sheet = (p: HTMLElement | null) => !!p && getComputedStyle(p).getPropertyValue('--sheet').trim() === '1';
      const rect = (p: HTMLElement | null) => (p ? p.getBoundingClientRect() : null);
      const leftEl = get('.panel.left');
      const rightEl = get('.panel.right');
      const left = sheet(leftEl) ? null : rect(leftEl);
      const right = sheet(rightEl) ? null : rect(rightEl);
      const top = rect(get('.topbar'));
      // The bottom inset clears the transport and, when it's open, the trace strip above it, a sheet over them, and
      // Words into maps' slice strip (features/Text.tsx), so the cortex is framed above it.
      const bottom = [get('.timeline'), get('.traces'), get('.text-board.strip'), sheet(leftEl) ? leftEl : null, sheet(rightEl) ? rightEl : null]
        .map(rect)
        .reduce<DOMRect | null>((a, r) => (r && (!a || r.top < a.top) ? r : a), null);
      setInsets({
        top: top ? Math.round(top.bottom - base.top) : 0,
        left: left ? Math.round(left.right - base.left) : 0,
        right: right ? Math.round(base.right - right.left) : 0,
        bottom: bottom ? Math.round(base.bottom - bottom.top) : 0,
      });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-presenting'] });
    measure();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [root, setInsets]);
}

export function App() {
  const root = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const scenario = useAtlas((s) => s.scenario);
  const presenting = useAtlas((s) => s.presenting);
  useShortcuts();
  useInsets(root);
  useEffect(() => {
    if (!canvas.current) return;
    void startViewport(canvas.current, { forceMock: params.get('engine') === 'mock' });
    startClock();
    startSims();
    startLive();
  }, []);
  return (
    <TipProvider>
      <IconProvider>
        <div ref={root} className="atlas" data-scenario={scenario} data-presenting={presenting}>
          <canvas ref={canvas} className="viewport" />
          <Loading />
          <ElectrodeSites />
          <Labels />
          <SectionMarks />
          <Journey />
          <TextBoard />
          <HoverCard />
          <TopBar />
          <LeftPanel />
          <Inspector />
          <div className="bottom">
            <Traces />
            <Methods />
            <Timeline />
            <Hud />
          </div>
          <PresentHint />
          <Palette />
          <Help />
          <Gallery />
          <Tour />
          <Highlights />
          <MoleculeDriver />
          <GpuNotice />
          <Toasts />
        </div>
      </IconProvider>
    </TipProvider>
  );
}
